import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ToastMessages } from "../../../../components/toasts/toastMessages";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import type { SingleCoverageInformation } from "../../../../data-model/coverageInformation";
import { CoverageStatus } from "../../../../enums/coverageStatus";
import { ManualEvEditClickSource } from "../../../../enums/eventsData";
import { showApiError } from "../../../../utils/showApiError";
import { EV_COVERAGE_REQUIRE_FIELDS_ON_SAVE } from "../../config/evCoverageEditFlags";
import type { PlanActivityStatus } from "../../helpers/coverageStatusUtils";
import {
  EvCoverageValidationIssue,
  getEvCoverageSaveValidationToastMessage,
  validateEvCoverageEditSnapshot,
  type EvCoverageEditValidationResult,
  type EvCoverageFieldId,
} from "../../helpers/evCoverageEditValidation";
import { seedEmptyPatientResponsibilityIfNeeded } from "../../helpers/seedEvCoveragePatientResponsibility";
import { usePersistEvCoverageEdits } from "../../hooks/usePersistEvCoverageEdits";
import { isManualBvEntryStatus } from "./evCoverageStatusConfig";
import {
  useEvCoverageLocalEditState,
  type UseEvCoverageLocalEditStateResult,
} from "./useEvCoverageLocalEditState";

export interface UseEvCoverageEditOrchestrationArgs {
  singleCoverage: SingleCoverageInformation;
  coverageStatus: CoverageStatus;
  isActive: PlanActivityStatus;
  isManuallyUpdated: boolean;
  serviceTypeCodeLabelMap?: ReadonlyMap<string, string>;
  orderId: string;
  orgId: string;
  coverageType: string;
  requireBvFields: boolean;
  requireCopayCoinsurance: boolean;
  hasAttemptedContinue: boolean;
  continueValidationAttemptId: number;
  autoEnterEditOnContinueFailure: boolean;
  focusTreatmentTabIndex?: number;
  onCoverageSaved?: () => void;
  onManualEvEditClicked?: (source: ManualEvEditClickSource) => void;
  onManualEvOverrideSaved?: () => void;
  onEditingChange?: (isEditing: boolean) => void;
  onContinueValidationDismiss?: () => void;
}

export interface UseEvCoverageEditOrchestrationResult {
  displayCoverage: SingleCoverageInformation;
  isEditing: boolean;
  patchPlanInformation: UseEvCoverageLocalEditStateResult["patchPlanInformation"];
  applyCoverageEdit: UseEvCoverageLocalEditStateResult["applyCoverageEdit"];
  treatmentTab: string;
  setTreatmentTab: (value: string) => void;
  showManualCoverageEditor: boolean;
  showInactiveOverrideEditor: boolean;
  showFieldErrors: boolean;
  planSummaryEnableEdit: boolean;
  editValidation: EvCoverageEditValidationResult;
  isPersisting: boolean;
  handleSaveEdit: () => Promise<void>;
  handleCancelEdit: () => void;
  handleBeginEdit: () => void;
  openManualCoverageEditor: () => void;
}

const VALID_EDIT_VALIDATION: EvCoverageEditValidationResult = {
  valid: true,
  invalidFieldIds: new Set<EvCoverageFieldId>(),
  issues: new Set<EvCoverageValidationIssue>(),
};

/**
 * Owns the EV coverage edit state machine: local draft editing, manual/inactive
 * override shells, required-field validation, persistence, and the auto-enter-edit
 * flow after a failed parent Continue validation. Keeps the unified content component
 * focused on rendering.
 *
 * @input args: Coverage snapshot, status, persistence identifiers, and parent callbacks.
 * @return UseEvCoverageEditOrchestrationResult: Edit state, derived flags, and handlers.
 */
export function useEvCoverageEditOrchestration(
  args: UseEvCoverageEditOrchestrationArgs,
): UseEvCoverageEditOrchestrationResult {
  const {
    singleCoverage,
    coverageStatus,
    isActive,
    isManuallyUpdated,
    serviceTypeCodeLabelMap,
    orderId,
    orgId,
    coverageType,
    requireBvFields,
    requireCopayCoinsurance,
    hasAttemptedContinue,
    continueValidationAttemptId,
    autoEnterEditOnContinueFailure,
    focusTreatmentTabIndex,
    onCoverageSaved,
    onManualEvEditClicked,
    onManualEvOverrideSaved,
    onEditingChange,
    onContinueValidationDismiss,
  } = args;

  const [treatmentTab, setTreatmentTab] = useState("0");
  const [showManualCoverageEditor, setShowManualCoverageEditor] =
    useState(false);
  const [showInactiveOverrideEditor, setShowInactiveOverrideEditor] =
    useState(false);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const { siblingOrders } = useDataAccumulatorForSingleOrder();
  useEffect(() => {
    if (!isManualBvEntryStatus(coverageStatus)) {
      setShowManualCoverageEditor(false);
    }
  }, [coverageStatus]);

  useEffect(() => {
    if (isActive !== "inactive") {
      setShowInactiveOverrideEditor(false);
    }
  }, [isActive]);

  const requireFieldsOnSave = EV_COVERAGE_REQUIRE_FIELDS_ON_SAVE;
  const showFieldErrors =
    requireFieldsOnSave && (hasAttemptedSave || hasAttemptedContinue);

  const isManualBvEntryState =
    isManualBvEntryStatus(coverageStatus) && !isManuallyUpdated;
  const isMedicaidTier = !!singleCoverage.medicaidInformation;
  const planSummaryEnableEdit =
    isMedicaidTier || !isManualBvEntryState || showManualCoverageEditor;

  const prepareWorkingCopyForEdit = useCallback(
    (draft: SingleCoverageInformation) => {
      seedEmptyPatientResponsibilityIfNeeded(draft, serviceTypeCodeLabelMap);
    },
    [serviceTypeCodeLabelMap],
  );

  const coverageEdit = useEvCoverageLocalEditState({
    singleCoverage,
    treatmentTabKey: treatmentTab,
    prepareWorkingCopyForEdit,
  });

  useEffect(() => {
    onEditingChange?.(coverageEdit.isEditing);
    return () => {
      onEditingChange?.(false);
    };
  }, [coverageEdit.isEditing, onEditingChange]);

  useEffect(() => {
    if (focusTreatmentTabIndex === undefined) {
      return;
    }
    setTreatmentTab(String(focusTreatmentTabIndex));
  }, [focusTreatmentTabIndex]);

  const activeTreatmentTabIndex = Number.parseInt(treatmentTab, 10);

  const validationOptions = useMemo(
    () => ({
      includePlanNameAndMemberId: true,
      activeTreatmentTabIndex: Number.isNaN(activeTreatmentTabIndex)
        ? 0
        : activeTreatmentTabIndex,
      requireBvFields,
      requireCopayCoinsurance,
    }),
    [activeTreatmentTabIndex, requireBvFields, requireCopayCoinsurance],
  );

  const editValidation = useMemo((): EvCoverageEditValidationResult => {
    if (!showFieldErrors) {
      return VALID_EDIT_VALIDATION;
    }
    return validateEvCoverageEditSnapshot(
      coverageEdit.displayCoverage,
      validationOptions,
    );
  }, [coverageEdit.displayCoverage, showFieldErrors, validationOptions]);

  const { persistCoverageEdit, isPersisting } = usePersistEvCoverageEdits({
    orderId,
    orgId,
    evCoverageStatus: coverageStatus,
    coverageType,
    siblingOrders: siblingOrders?.siblings ?? [],
    // Refetch is deferred until after template refresh in handleSaveEdit.
  });

  const handleSaveEdit = useCallback(async () => {
    if (requireFieldsOnSave) {
      setHasAttemptedSave(true);
      const result = validateEvCoverageEditSnapshot(
        coverageEdit.displayCoverage,
        validationOptions,
      );
      if (!result.valid) {
        toast.error(getEvCoverageSaveValidationToastMessage(result.issues));
        return;
      }
    }

    try {
      await coverageEdit.saveEdit(persistCoverageEdit);
    } catch (error) {
      showApiError(error, ToastMessages.COVERAGE_SAVE_FAILURE);
      return;
    }

    onCoverageSaved?.();
    onManualEvOverrideSaved?.();
    setHasAttemptedSave(false);
    toast.success(ToastMessages.COVERAGE_SAVE_SUCCESS);
  }, [
    coverageEdit,
    onCoverageSaved,
    onManualEvOverrideSaved,
    persistCoverageEdit,
    requireFieldsOnSave,
    orderId,
    validationOptions,
  ]);

  const handleCancelEdit = useCallback(() => {
    setHasAttemptedSave(false);
    coverageEdit.cancelEdit();
    setShowManualCoverageEditor(false);
    setShowInactiveOverrideEditor(false);
    if (hasAttemptedContinue) {
      onContinueValidationDismiss?.();
    }
  }, [
    coverageEdit.cancelEdit,
    hasAttemptedContinue,
    onContinueValidationDismiss,
  ]);

  const handleBeginEdit = useCallback(() => {
    onManualEvEditClicked?.(ManualEvEditClickSource.EDIT_BUTTON);
    setHasAttemptedSave(false);
    if (isActive === "inactive") {
      setShowInactiveOverrideEditor(true);
    }
    coverageEdit.beginEdit();
  }, [coverageEdit.beginEdit, isActive, onManualEvEditClicked]);

  const enterEditModeFromContinueValidation = useCallback(() => {
    setHasAttemptedSave(false);
    if (isManualBvEntryState) {
      setShowManualCoverageEditor(true);
      coverageEdit.beginEdit();
      return;
    }
    if (isActive === "inactive") {
      setShowInactiveOverrideEditor(true);
    }
    coverageEdit.beginEdit();
  }, [coverageEdit.beginEdit, isActive, isManualBvEntryState]);

  const consumedAutoEditAttemptRef = useRef(0);

  useEffect(() => {
    if (
      !autoEnterEditOnContinueFailure ||
      !hasAttemptedContinue ||
      continueValidationAttemptId === 0 ||
      consumedAutoEditAttemptRef.current === continueValidationAttemptId
    ) {
      return;
    }

    const targetTreatmentTab =
      focusTreatmentTabIndex !== undefined
        ? String(focusTreatmentTabIndex)
        : treatmentTab;

    if (treatmentTab !== targetTreatmentTab) {
      return;
    }

    if (coverageEdit.isEditing) {
      consumedAutoEditAttemptRef.current = continueValidationAttemptId;
      return;
    }

    consumedAutoEditAttemptRef.current = continueValidationAttemptId;
    enterEditModeFromContinueValidation();
  }, [
    autoEnterEditOnContinueFailure,
    continueValidationAttemptId,
    coverageEdit.isEditing,
    enterEditModeFromContinueValidation,
    focusTreatmentTabIndex,
    hasAttemptedContinue,
    treatmentTab,
  ]);

  const openManualCoverageEditor = useCallback(() => {
    onManualEvEditClicked?.(ManualEvEditClickSource.UPDATE_MANUALLY);
    setHasAttemptedSave(false);
    setShowManualCoverageEditor(true);
    coverageEdit.beginEdit();
  }, [coverageEdit.beginEdit, onManualEvEditClicked]);

  return {
    displayCoverage: coverageEdit.displayCoverage,
    isEditing: coverageEdit.isEditing,
    patchPlanInformation: coverageEdit.patchPlanInformation,
    applyCoverageEdit: coverageEdit.applyCoverageEdit,
    treatmentTab,
    setTreatmentTab,
    showManualCoverageEditor,
    showInactiveOverrideEditor,
    showFieldErrors,
    planSummaryEnableEdit,
    editValidation,
    isPersisting,
    handleSaveEdit,
    handleCancelEdit,
    handleBeginEdit,
    openManualCoverageEditor,
  };
}
